import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view';

type PlaceholderSpec = {
	uploadId: string;
	/** The scrim element, carried on the decoration so progress needs no side table. */
	progressElement: HTMLElement;
};

type ImageUploadMeta =
	| { type: 'add'; uploadId: string; pos: number; previewUrl: string }
	| { type: 'remove'; uploadId: string };

export const imageUploadPluginKey = new PluginKey<DecorationSet>('rte-image-upload');

let uploadCounter = 0;

export const createUploadId = (): string => {
	uploadCounter += 1;
	return `rte-image-upload-${uploadCounter}`;
};

const buildPlaceholder = (previewUrl: string): { root: HTMLElement; scrim: HTMLElement } => {
	const root = document.createElement('span');
	root.className = 'rte-image-uploading relative inline-block align-bottom';

	const img = document.createElement('img');
	img.src = previewUrl;
	img.alt = '';
	img.className = 'max-w-full rounded-lg opacity-60';
	root.appendChild(img);

	const scrim = document.createElement('span');
	scrim.className =
		'absolute inset-0 flex items-center justify-center rounded-lg bg-black/30 text-sm font-medium text-white';
	scrim.textContent = '0%';
	root.appendChild(scrim);

	return { root, scrim };
};

const findPlaceholder = (decorations: DecorationSet, uploadId: string) =>
	decorations.find(undefined, undefined, (spec) => spec.uploadId === uploadId);

export const createImageUploadPlugin = (): Plugin<DecorationSet> =>
	new Plugin<DecorationSet>({
		key: imageUploadPluginKey,
		state: {
			init: () => DecorationSet.empty,
			apply(tr, decorations) {
				const mapped = decorations.map(tr.mapping, tr.doc);
				const meta = tr.getMeta(imageUploadPluginKey) as ImageUploadMeta | undefined;

				if (!meta) return mapped;

				if (meta.type === 'add') {
					const { root, scrim } = buildPlaceholder(meta.previewUrl);
					const spec: PlaceholderSpec = { uploadId: meta.uploadId, progressElement: scrim };
					return mapped.add(tr.doc, [Decoration.widget(meta.pos, root, spec)]);
				}

				const existing = findPlaceholder(mapped, meta.uploadId);
				return existing.length ? mapped.remove(existing) : mapped;
			},
		},
		props: {
			decorations(state) {
				return imageUploadPluginKey.getState(state);
			},
		},
	});

const dispatchMeta = (view: EditorView, meta: ImageUploadMeta) =>
	view.dispatch(view.state.tr.setMeta(imageUploadPluginKey, meta));

export const addImagePlaceholder = (
	view: EditorView,
	uploadId: string,
	pos: number,
	previewUrl: string
): void => dispatchMeta(view, { type: 'add', uploadId, pos, previewUrl });

export const updateImagePlaceholderProgress = (
	view: EditorView,
	uploadId: string,
	percent: number
): void => {
	const decorations = imageUploadPluginKey.getState(view.state);
	if (!decorations) return;

	const [placeholder] = findPlaceholder(decorations, uploadId);
	const spec = placeholder?.spec as PlaceholderSpec | undefined;

	if (spec) spec.progressElement.textContent = `${Math.round(percent)}%`;
};

/** Removes the placeholder and returns the position it drifted to, or null if it is already gone. */
export const removeImagePlaceholder = (view: EditorView, uploadId: string): number | null => {
	const decorations = imageUploadPluginKey.getState(view.state);
	const [placeholder] = findPlaceholder(decorations ?? DecorationSet.empty, uploadId);

	if (!placeholder) return null;

	dispatchMeta(view, { type: 'remove', uploadId });

	return placeholder.from;
};
